/**
 * Global keyboard shortcuts.
 *
 * Linear-style two-step sequences ("g then d" for "go to dashboard")
 * plus single-key actions. Disabled whenever the user is typing in an
 * input / textarea / contenteditable so we don't hijack normal typing.
 *
 *   ?         show this help overlay (toggle)
 *   /         focus the page's primary search input (if any)
 *   g d       go to /
 *   g i       go to /inbox
 *   g s       go to /settings
 *   n         on /jobs/:id, open New Brief
 *   esc       (handled per-component)
 *
 * Cmd/Ctrl+K is owned by CommandPalette; we don't re-bind it here.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { classNames } from '@/lib/format';
import { isPictureUpPage } from '@/lib/pu-class';

const SEQUENCE_TIMEOUT_MS = 1200;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function KeyboardShortcuts() {
  const navigate = useNavigate();
  const location = useLocation();
  const pendingRef = useRef<string | null>(null);
  const pendingTimerRef = useRef<number | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const clearPending = () => {
      pendingRef.current = null;
      if (pendingTimerRef.current !== null) {
        window.clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
    };

    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      // CRM shortcuts (g d opens the Kilogram dashboard, ? its help).
      // PictureUp pages, share links included, are not the CRM: only
      // "/" (focus the search field) carries over.
      if (isPictureUpPage(location.pathname) && e.key !== '/') return;

      // Single-key actions
      if (e.key === '?') {
        e.preventDefault();
        setHelpOpen((x) => !x);
        return;
      }
      if (e.key === 'Escape' && helpOpen) {
        setHelpOpen(false);
        return;
      }
      if (e.key === '/') {
        const focusable = document.querySelector<HTMLInputElement>(
          'input[type="search"], [data-shortcut="search"]',
        );
        if (focusable) {
          e.preventDefault();
          focusable.focus();
        }
        return;
      }
      if (e.key === 'n') {
        const jobMatch = location.pathname.match(/^\/jobs\/([^/]+)/);
        if (jobMatch) {
          e.preventDefault();
          navigate(`/jobs/${jobMatch[1]}/briefs/new`);
        }
        return;
      }

      // Sequences starting with 'g'
      if (pendingRef.current === 'g') {
        if (e.key === 'd') {
          e.preventDefault();
          navigate('/');
        } else if (e.key === 'i') {
          e.preventDefault();
          navigate('/inbox');
        } else if (e.key === 's') {
          e.preventDefault();
          navigate('/settings');
        }
        clearPending();
        return;
      }

      if (e.key === 'g') {
        pendingRef.current = 'g';
        pendingTimerRef.current = window.setTimeout(clearPending, SEQUENCE_TIMEOUT_MS);
      }
    };

    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
      clearPending();
    };
  }, [helpOpen, location.pathname, navigate]);

  if (!helpOpen) return null;
  return <HelpOverlay onClose={() => setHelpOpen(false)} />;
}

function HelpOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-label="Keyboard shortcuts"
      className="fixed inset-0 z-[65] flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0 bg-kg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="kg-pane relative max-w-md w-full p-6 text-kg-black dark:text-kg-grey-100">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className={classNames('font-medium text-lg')}>Keyboard shortcuts</h2>
          <button onClick={onClose} className="kg-btn-ghost">
            close
          </button>
        </div>
        <ul className="space-y-2.5 text-sm">
          <Row label="Command palette" combo={['⌘', 'K']} />
          <Row label="Go to Dashboard" combo={['G', 'D']} />
          <Row label="Go to Inbox" combo={['G', 'I']} />
          <Row label="Go to Settings" combo={['G', 'S']} />
          <Row label="New brief (on a job page)" combo={['N']} />
          <Row label="Focus search (where available)" combo={['/']} />
          <Row label="Show / hide this help" combo={['?']} />
        </ul>
      </div>
    </div>
  );
}

function Row({ label, combo }: { label: string; combo: string[] }) {
  return (
    <li className="flex items-center justify-between gap-3">
      <span className="text-kg-grey-800 dark:text-kg-grey-100">{label}</span>
      <span className="flex items-center gap-1">
        {combo.map((c, i) => (
          <span key={i} className="kg-kbd">
            {c}
          </span>
        ))}
      </span>
    </li>
  );
}
